import { Injectable } from '@angular/core';

import { cloneDeep } from 'lodash';
import { v4 as UUID } from 'uuid';

import { Project, SelectableField } from '../../models';
import { FORM_CONSTANTS } from '../../shared/constants';
import { FieldType } from '../../shared/enums';
import { ColumnTypes } from './enums/column-types';
import { Column, Field, Section, SmartsForm } from './models';

@Injectable({
    providedIn: 'root',
})
export class ProjectSummaryConfigurationLogicService {

    initializeSummaryProject(project: Project): Project {
        project.isSummaryProject = true;
        project.active ??= false;
        project.JSONForms ??= new SmartsForm();
        project.layoutDraft ??= new SmartsForm();
        project.JSONForms.Sections ??= [];
        project.layoutDraft.Sections ??= [];
        if (project.layoutDraft.Sections.length === 0) {
            project.layoutDraft.Sections.push(this.createSummarySection());
        }

        this.repairContentOrder(project.layoutDraft.Sections);

        return project;
    }

    addSummarySection(project: Project): Section | null {
        const summaryProject = this.initializeSummaryProject(project);
        const layoutDraft = summaryProject.layoutDraft;
        if (!layoutDraft) {
            return null;
        }

        const section = this.createSummarySection();
        layoutDraft.Sections ??= [];
        layoutDraft.Sections.push(section);

        return section;
    }

    addSummaryColumn(section: Section, startsNewRow = false): Column {
        return this.addSummaryColumnToSection(section, ColumnTypes.Column, this.getSummaryColumnRow(section, startsNewRow));
    }

    addSummaryColumnToRow(section: Section, row: number): Column {
        return this.addSummaryColumnToSection(section, ColumnTypes.Column, row);
    }

    addSummaryColumnGroup(section: Section, startsNewRow = false): Column {
        return this.addSummaryColumnToSection(section, ColumnTypes.Group, this.getSummaryColumnRow(section, startsNewRow));
    }

    addSummaryColumnGroupToRow(section: Section, row: number): Column {
        return this.addSummaryColumnToSection(section, ColumnTypes.Group, row);
    }

    private addSummaryColumnToSection(section: Section, type: ColumnTypes.Column | ColumnTypes.Group, row: number): Column {
        const column = this.createSummaryColumn(type);
        if (type === ColumnTypes.Group) {
            column.GroupTitle = '';
        }

        section.Columns ??= [];
        column.row = row;
        section.Columns.push(column);
        this.addSummaryContentOrder(section, row);

        return column;
    }

    removeSummaryColumn(section: Section, columnId: string): void {
        section.Columns = (section.Columns ?? []).filter(column => column.id !== columnId);
        this.ensureContentOrder(section);
    }

    promoteColumnForGrid(section: Section, column: Column): void {
        const columns = section.Columns ?? [];
        const currentRow = column.row ?? 0;
        const hasGridField = (column.Fields ?? []).some((field: Field) => field.FieldType?.id === FieldType.Grid);
        const rowColumns = columns.filter((candidate: Column) => (candidate.row ?? 0) === currentRow);
        const columnIndex = rowColumns.findIndex((candidate: Column) => candidate.id === column.id);
        const columnsBeforeGrid = rowColumns.slice(0, columnIndex);
        const columnsAfterGrid = rowColumns.slice(columnIndex + 1);

        if (!hasGridField || rowColumns.length < 2) {
            return;
        }

        const gridRow = columnsBeforeGrid.length > 0 ? currentRow + 1 : currentRow;
        const afterGridRow = gridRow + (columnsAfterGrid.length > 0 ? 1 : 0);
        const rowsToShift = (columnsBeforeGrid.length > 0 ? 1 : 0) + (columnsAfterGrid.length > 0 ? 1 : 0);

        columns.forEach((candidate: Column) => {
            if (!rowColumns.includes(candidate) && (candidate.row ?? 0) >= gridRow) {
                candidate.row = (candidate.row ?? 0) + rowsToShift;
            }
        });

        column.row = gridRow;
        columnsAfterGrid.forEach((candidate: Column) => {
            candidate.row = afterGridRow;
        });
        this.updateSummaryContentOrderForGridSplit(section, currentRow, gridRow, afterGridRow, columnsAfterGrid.length > 0, rowsToShift);
    }

    private updateSummaryContentOrderForGridSplit(
        section: Section,
        currentRow: number,
        gridRow: number,
        afterGridRow: number,
        hasAfterRow: boolean,
        rowsToShift: number
    ): void {
        section.contentOrder ??= [];
        const currentRowToken = `row:${currentRow}`;
        const currentRowIndex = section.contentOrder.indexOf(currentRowToken);

        const contentOrder = section.contentOrder.map((token: string) => {
            if (!token.startsWith('row:')) {
                return token;
            }

            const row = Number(token.slice(4));
            return row > currentRow ? `row:${row + rowsToShift}` : token;
        });

        const insertedRows = [`row:${gridRow}`];
        if (hasAfterRow) {
            insertedRows.push(`row:${afterGridRow}`);
        }
        const insertIndex = currentRowIndex >= 0 ? currentRowIndex + 1 : contentOrder.length;
        contentOrder.splice(insertIndex, 0, ...insertedRows.filter((token: string) => !contentOrder.includes(token)));
        if (!contentOrder.includes(currentRowToken)) {
            contentOrder.unshift(currentRowToken);
        }
        section.contentOrder = contentOrder;
    }

    getProjectIdsSummaryProjectConsumesFrom(project: Project): string[] {
        const projectIds = new Set<string>();
        this.collectFieldProjectIds(project.layoutDraft?.Sections ?? [], projectIds);
        this.collectProjectNotesProjectIds(project, projectIds);
        return Array.from(projectIds);
    }

    getFieldFromProjectJSONForm(selectableField: SelectableField, projectId: string | undefined, projects: Project[]): Field {
        const project = projects.find((candidateProject: Project) => candidateProject.id === projectId);
        const fieldId = selectableField.identifier?.fieldId;
        const projectField = fieldId ? this.findObjectById(project?.JSONForms, fieldId) : undefined;

        const copiedField = projectField
            ? cloneDeep(projectField) as unknown as Field
            : {
                id: fieldId ?? selectableField.hash,
                Name: selectableField.name,
                Path: fieldId,
            } as Field;
        copiedField.projectId = projectId;
        return copiedField;
    }

    private findObjectById(value: unknown, id: string): Record<string, unknown> | undefined {
        if (Array.isArray(value)) {
            for (const item of value) {
                const match = this.findObjectById(item, id);
                if (match) {
                    return match;
                }
            }
            return undefined;
        }

        if (!value || typeof value !== 'object') {
            return undefined;
        }

        const objectValue = value as Record<string, unknown>;
        if (objectValue['id'] === id) {
            return objectValue;
        }

        for (const childValue of Object.values(objectValue)) {
            const match = this.findObjectById(childValue, id);
            if (match) {
                return match;
            }
        }

        return undefined;
    }

    removeSummarySection(project: Project, sectionId: string): boolean {
        const sections = project.layoutDraft?.Sections;
        if (!sections) {
            return false;
        }

        return this.removeSectionFromList(sections, sectionId);
    }

    moveSummarySection(project: Project, sectionId: string, direction: 'up' | 'down'): boolean {
        const sections = project.layoutDraft?.Sections;
        if (!sections) {
            return false;
        }

        return this.moveSectionInList(sections, sectionId, direction);
    }

    addSummarySubsection(section: Section): Section {
        const subsection = this.createSummarySection();
        subsection.parentId = section.id;
        section.Sections ??= [];
        section.Sections.push(subsection);
        this.ensureContentOrder(section);

        return subsection;
    }

    private addSummaryContentOrder(section: Section, row: number): void {
        const contentOrder = this.ensureContentOrder(section);
        const rowToken = `row:${row}`;
        if (!contentOrder.includes(rowToken)) {
            contentOrder.push(rowToken);
        }
    }

    private repairContentOrder(sections: Section[]): void {
        sections.forEach((section: Section) => {
            this.ensureContentOrder(section);
            this.repairContentOrder(section.Sections ?? []);
        });
    }

    private ensureContentOrder(section: Section): string[] {
        const requiredTokens = this.getRequiredContentTokens(section);
        const existingTokens = (section.contentOrder ?? []).filter((token: string) => requiredTokens.includes(token));

        requiredTokens.forEach((token: string) => {
            if (!existingTokens.includes(token)) {
                existingTokens.push(token);
            }
        });

        section.contentOrder = existingTokens;
        return existingTokens;
    }

    private getRequiredContentTokens(section: Section): string[] {
        const rows = new Set<number>();
        (section.Columns ?? []).forEach((column: Column, columnIndex: number) => {
            rows.add(column.row ?? Math.floor(columnIndex / FORM_CONSTANTS.DEFAULT_MAX_COLUMNS));
        });

        const rowTokens = Array.from(rows).sort((first, second) => first - second).map((row: number) => `row:${row}`);
        const subsectionTokens = (section.Sections ?? []).map((subsection: Section) => `section:${subsection.id}`);

        return [...rowTokens, ...subsectionTokens];
    }

    private createSummarySection(): Section {
        return {
            id: UUID(),
            type: ColumnTypes.Section,
            SectionTitle: '',
            Columns: [],
            Sections: []
        };
    }

    private collectFieldProjectIds(sections: Section[], projectIds: Set<string>): void {
        sections.forEach((section: Section) => {
            (section.Columns ?? []).forEach((column: Column) => {
                (column.Fields ?? []).forEach((field: Field) => {
                    if (field.projectId) {
                        projectIds.add(field.projectId);
                    }
                });
            });
            this.collectFieldProjectIds(section.Sections ?? [], projectIds);
        });
    }

    private collectProjectNotesProjectIds(project: Project, projectIds: Set<string>): void {
        project.projectNotes?.forEach((projectNotesByProjectId: { [projectId: string]: string[]; }) => {
            Object.keys(projectNotesByProjectId).forEach((projectId: string) => {
                if (projectId) {
                    projectIds.add(projectId);
                }
            });
        });
    }

    private removeSectionFromList(sections: Section[], sectionId: string, parentSection?: Section): boolean {
        const sectionIndex = sections.findIndex(section => section.id === sectionId);
        if (sectionIndex !== -1) {
            sections.splice(sectionIndex, 1);
            if (parentSection) {
                this.ensureContentOrder(parentSection);
            }
            return true;
        }

        return sections.some(section => this.removeSectionFromList(section.Sections ?? [], sectionId, section));
    }

    private moveSectionInList(sections: Section[], sectionId: string, direction: 'up' | 'down', parentSection?: Section): boolean {
        const sectionIndex = sections.findIndex(section => section.id === sectionId);
        if (sectionIndex !== -1) {
            const targetIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
            if (targetIndex < 0 || targetIndex >= sections.length) {
                return false;
            }

            [sections[sectionIndex], sections[targetIndex]] = [sections[targetIndex], sections[sectionIndex]];
            if (parentSection) {
                this.syncSubsectionContentOrder(parentSection);
            }
            return true;
        }

        return sections.some(section => this.moveSectionInList(section.Sections ?? [], sectionId, direction, section));
    }

    private syncSubsectionContentOrder(section: Section): void {
        const subsectionTokens = (section.Sections ?? []).map(subsection => `section:${subsection.id}`);
        let subsectionIndex = 0;

        section.contentOrder = (section.contentOrder ?? []).map((token: string) => {
            if (!token.startsWith('section:') || subsectionIndex >= subsectionTokens.length) {
                return token;
            }

            return subsectionTokens[subsectionIndex++];
        });

        section.contentOrder.push(...subsectionTokens.slice(subsectionIndex));
    }

    private createSummaryColumn(type: ColumnTypes.Column | ColumnTypes.Group): Column {
        return {
            id: UUID(),
            type,
            Fields: []
        };
    }

    private getSummaryColumnRow(section: Section, startsNewRow: boolean): number {
        const columns = section.Columns ?? [];
        if (columns.length === 0) {
            return 0;
        }

        const rowCounts = new Map<number, number>();
        columns.forEach((column, columnIndex) => {
            const row = column.row ?? Math.floor(columnIndex / FORM_CONSTANTS.DEFAULT_MAX_COLUMNS);
            rowCounts.set(row, (rowCounts.get(row) ?? 0) + 1);
        });

        const lastRow = Math.max(...rowCounts.keys());
        if (startsNewRow || (rowCounts.get(lastRow) ?? 0) >= FORM_CONSTANTS.DEFAULT_MAX_COLUMNS) {
            return lastRow + 1;
        }

        return lastRow;
    }
}
